import { Button } from '@hologram-dimension/button';
import { Form } from '@hologram-dimension/form';
import { clsx } from 'clsx/lite';
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StoredFilters } from 'state/SimInventory/SimInventoryStore';
import SimInventoryStoreContext from 'state/SimInventory/SimInventoryStoreContext';
import useSimInventoryStoredFilters from 'state/SimInventory/useSimInventoryStoredFilters';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import type { OnSimsModified, SimInventorySegmentName } from 'types/common';
import { hasUserFiltersForSegment } from 'utils/filters';
import { useThrottleCallback } from 'utils/hooks';
import {
  ActivationDateFilter,
  DeviceNameFilter,
  ICCIDFilter,
  IMEIFilter,
  LastActiveDateFilter,
  OrganizationFilter,
  SimPlanFilter,
  SimStatusFilter,
  SimTagFilter,
  TargetActivationDateFilter,
} from './Filters';
import styles from './SimInventoryFilters.module.css';
import type { InputFilters } from './shared';
import {
  defaultFilterValues,
  filterLabels,
  getDefaultValueForFilter,
  getDefaultValues,
} from './shared';

interface SimInventoryFiltersProps {
  className?: string;
  segmentName: SimInventorySegmentName;
}

const SimInventoryFilters = forwardRef<OnSimsModified, SimInventoryFiltersProps>(
  ({ className, segmentName }, ref) => {
    const simInventoryStore = useContext(SimInventoryStoreContext);
    if (!simInventoryStore) {
      throw new Error('Not in SimInventoryStoreProvider context');
    }

    // Persisted state modification
    const {
      resetFilters,
      setOrganizationIdFilter,
      setDeviceNameFilter,
      setIccidFilter,
      setImeiFilter,
      setLastActiveFilter,
      setActivationDateFilter,
      setTargetActivationDateFilter,
      setSimStatusFilter,
      setTagFilter,
      setNoTagFilter,
      setPlanFilter,
    } = simInventoryStore();
    const storedFilters = useSimInventoryStoredFilters();

    const { sendAnalyticsEvent } = useAnalytics();
    const previousFiltersRef = useRef(storedFilters);

    useEffect(() => {
      const previousFilters = previousFiltersRef.current;
      const isEqual = (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b);
      const isEmptyArray = (a: any) => Array.isArray(a) && a.length === 0;

      // Compare the current and previous filters for event tracking purposes
      // We're only concerned about new values or changed values, not removed/cleared values
      (Object.keys(storedFilters) as Array<keyof StoredFilters>).forEach((key) => {
        if (
          (typeof previousFilters[key] === undefined ||
            !isEqual(previousFilters[key], storedFilters[key])) &&
          !isEmptyArray(storedFilters[key]) &&
          !isEqual(storedFilters[key], defaultFilterValues[key]) &&
          storedFilters[key]
        ) {
          if (sendAnalyticsEvent) {
            sendAnalyticsEvent(`Filter - ${filterLabels[key]} apply`, {
              value: storedFilters[key],
            });
          }
        }
      });

      // Update the ref for the next comparison
      previousFiltersRef.current = storedFilters;
    }, [storedFilters, sendAnalyticsEvent]);

    const areFiltersApplied = hasUserFiltersForSegment({ segmentName, filters: storedFilters });

    const storedFiltersToInputFilters = useCallback((filters?: StoredFilters) => {
      const inputFilters: InputFilters = getDefaultValues();

      if (!filters) {
        return inputFilters;
      }

      if (filters.organizationId) {
        inputFilters.organizationId = filters.organizationId.toString();
      }

      if (filters.deviceName) {
        inputFilters.deviceName = filters.deviceName;
      }

      if (filters.iccid?.value && filters.iccid.value.length > 0) {
        inputFilters.iccid = structuredClone(filters.iccid);
      }

      if (filters.imei?.value && filters.imei.value.length > 0) {
        inputFilters.imei = structuredClone(filters.imei);
      }

      if (filters.lastActive) {
        inputFilters.lastActive = structuredClone(filters.lastActive);
      }

      if (filters.activationDate) {
        inputFilters.activationDate = structuredClone(filters.activationDate);
      }

      if (filters.targetActivationDate) {
        inputFilters.targetActivationDate = structuredClone(filters.targetActivationDate);
      }

      if (filters.simStatuses && filters.simStatuses.length > 0) {
        inputFilters.simStatuses = structuredClone(filters.simStatuses);
      }

      if (filters.tags && filters.tags.length > 0) {
        inputFilters.tags = structuredClone(filters.tags).map((id) => `${id}`);
      }

      if (filters.noTags) {
        inputFilters.noTags = filters.noTags;
      }

      if (filters.plans && filters.plans.length > 0) {
        inputFilters.plans = structuredClone(filters.plans).map((id) => `${id}`);
      }

      return inputFilters;
    }, []);

    // Local state
    const form = useForm<InputFilters>({
      defaultValues: storedFiltersToInputFilters(storedFilters),
      mode: 'onSubmit',
      reValidateMode: 'onSubmit',
      shouldFocusError: false,
    });

    const onSubmit: SubmitHandler<InputFilters> = useCallback(
      (data) => {
        setDeviceNameFilter(data.deviceName ?? undefined);

        setIccidFilter(data.iccid?.value ? data.iccid : undefined);

        setImeiFilter(data.imei?.value ? data.imei : undefined);

        setLastActiveFilter(data.lastActive ?? undefined);

        setActivationDateFilter(data.activationDate ?? undefined);

        setTargetActivationDateFilter(data.targetActivationDate ?? undefined);

        setSimStatusFilter(data.simStatuses ?? undefined);

        setTagFilter(data.tags ? data.tags.map((tagId) => parseInt(tagId, 10)) : undefined);

        setNoTagFilter(data.noTags ?? undefined);

        setPlanFilter(data.plans ? data.plans.map((planId) => parseInt(planId, 10)) : undefined);

        if (segmentName !== 'all') {
          setOrganizationIdFilter(undefined);
        } else if (data.organizationId) {
          setOrganizationIdFilter(parseInt(data.organizationId, 10));

          // TODO: We can, perhaps, get smarter with this later when we have multiple organizations. But for now this has to go when the org is changed or cleared.
          if (storedFilters.organizationId !== parseInt(data.organizationId, 10)) {
            setTagFilter(undefined);
          }
        } else {
          setOrganizationIdFilter(undefined);

          if (storedFilters.organizationId) {
            setTagFilter(undefined);
          }
        }
      },
      [
        setDeviceNameFilter,
        setIccidFilter,
        setImeiFilter,
        setTargetActivationDateFilter,
        setLastActiveFilter,
        setActivationDateFilter,
        setSimStatusFilter,
        setTagFilter,
        setNoTagFilter,
        setPlanFilter,
        segmentName,
        setOrganizationIdFilter,
        storedFilters.organizationId,
      ]
    );

    const { handleSubmit, reset, clearErrors } = form;
    const submit = handleSubmit(onSubmit);

    const onReset = useCallback(() => {
      resetFilters();
      reset();
    }, [reset, resetFilters]);

    // 0 basically disables this, we can re-add it if we have performance problems,
    //  but it feels odd to delay the filter application
    const throttledSubmit = useThrottleCallback(submit, 0);

    // useEffect to detect whether the user is scrolling within the form
    // so we can apply certain CSS classes
    const filterWrapper = useRef<HTMLFormElement>(null);
    const [isScrolling, setIsScrolling] = useState<boolean>(false);

    const filtersDiv = filterWrapper.current;
    const handleScroll = useCallback(() => {
      if (filtersDiv) {
        setIsScrolling(filtersDiv.scrollTop > 0);
      }
    }, [filtersDiv]);

    useEffect(() => {
      filtersDiv?.addEventListener('scroll', handleScroll);

      // Clean up the event listener on component unmount
      return () => {
        filtersDiv?.removeEventListener('scroll', handleScroll);
      };
    }, [filterWrapper, filtersDiv, handleScroll]);

    // Update when the segment changes
    useEffect(() => {
      if (segmentName !== 'all') {
        setOrganizationIdFilter(undefined);
      }
      reset(storedFiltersToInputFilters(storedFilters));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segmentName, storedFiltersToInputFilters]);

    // Handle changes
    useEffect(() => {
      const simInventoryStoreUnsub = simInventoryStore.subscribe((state) => {
        const curFilters = state?.filters;

        reset(storedFiltersToInputFilters(curFilters));
      });

      return () => simInventoryStoreUnsub();
    }, [reset, simInventoryStore, storedFiltersToInputFilters]);

    // TODO: Probably move every one of these onClear* into their respective components and pull simInventoryStore sets into those directly. Also use default values for "clearing"
    const onClearSimStatusFilter = useCallback(() => {
      setSimStatusFilter(undefined);
    }, [setSimStatusFilter]);

    const onClearTagFilter = useCallback(() => {
      setTagFilter(undefined);
      setNoTagFilter(undefined);
    }, [setNoTagFilter, setTagFilter]);

    const onClearPlanFilter = useCallback(() => {
      setPlanFilter(undefined);
    }, [setPlanFilter]);

    const onClearIccidFilter = useCallback(() => {
      setIccidFilter(undefined);
    }, [setIccidFilter]);

    const onClearLastActiveFilter = useCallback(() => {
      clearErrors('lastActive');
      setLastActiveFilter(getDefaultValueForFilter('lastActive'));
    }, [clearErrors, setLastActiveFilter]);

    const onClearActivationDateFilter = useCallback(() => {
      clearErrors('activationDate');
      setActivationDateFilter(getDefaultValueForFilter('activationDate'));
    }, [clearErrors, setActivationDateFilter]);

    const onClearTargetActivationDateFilter = useCallback(() => {
      clearErrors('targetActivationDate');
      setTargetActivationDateFilter(getDefaultValueForFilter('targetActivationDate'));
    }, [clearErrors, setTargetActivationDateFilter]);

    const onClearImeiFilter = useCallback(() => {
      setImeiFilter(undefined);
    }, [setImeiFilter]);

    const onClearOrganizationFilter = useCallback(() => {
      setOrganizationIdFilter(undefined);
      setTagFilter(undefined);
    }, [setOrganizationIdFilter, setTagFilter]);

    const onClearDeviceNameFilter = useCallback(() => {
      setDeviceNameFilter(undefined);
    }, [setDeviceNameFilter]);

    // Refresh
    const refreshTagsRef = useRef<OnSimsModified>(null);
    const refreshPlansRef = useRef<OnSimsModified>(null);

    const onSimsModified = useCallback<OnSimsModified>((simsModified?: Array<number>) => {
      if (refreshTagsRef.current) {
        refreshTagsRef.current(simsModified);
      }

      if (refreshPlansRef.current) {
        refreshPlansRef.current(simsModified);
      }
    }, []);

    useImperativeHandle(ref, () => onSimsModified, [onSimsModified]);

    return (
      <Form
        className={clsx(styles.wrapper, className, isScrolling && styles.isScrolling)}
        onSubmit={submit}
        ref={filterWrapper}
      >
        <div className={styles.headerWrapper}>
          <div className={styles.headerLeftContents}>
            <span className={styles.header}>Filters</span>
          </div>
          <div className={styles.headerRightContents}>
            {areFiltersApplied && (
              <Button variant="tertiary" type="reset" aria-label="Reset filters" onClick={onReset}>
                Reset
              </Button>
            )}
          </div>
        </div>
        {segmentName === 'all' && (
          <div className={styles.contentWrapper}>
            <OrganizationFilter
              segmentName={segmentName}
              onClear={onClearOrganizationFilter}
              submit={throttledSubmit}
              form={form}
            />
          </div>
        )}
        <div className={styles.contentWrapper}>
          <SimTagFilter
            ref={refreshTagsRef}
            segmentName={segmentName}
            onClear={onClearTagFilter}
            submit={throttledSubmit}
            form={form}
          />
        </div>
        <div className={styles.contentWrapper}>
          <SimStatusFilter
            segmentName={segmentName}
            onClear={onClearSimStatusFilter}
            submit={throttledSubmit}
            form={form}
          />
        </div>
        <div className={styles.contentWrapper}>
          <ICCIDFilter
            segmentName={segmentName}
            onClear={onClearIccidFilter}
            submit={throttledSubmit}
            form={form}
          />
        </div>
        {/* TODO: Refactor the filters to spread the shared props (fewer lines of code) and use column definitions to make sure the column for the filter exists */}
        {segmentName !== 'deactivated' && (
          <div className={styles.contentWrapper}>
            <LastActiveDateFilter
              segmentName={segmentName}
              onClear={onClearLastActiveFilter}
              submit={throttledSubmit}
              form={form}
            />
          </div>
        )}
        <div className={styles.contentWrapper}>
          <DeviceNameFilter
            segmentName={segmentName}
            onClear={onClearDeviceNameFilter}
            submit={throttledSubmit}
            form={form}
          />
        </div>
        <div className={styles.contentWrapper}>
          <IMEIFilter
            segmentName={segmentName}
            onClear={onClearImeiFilter}
            submit={throttledSubmit}
            form={form}
          />
        </div>
        {segmentName !== 'deactivated' && (
          <div className={styles.contentWrapper}>
            <SimPlanFilter
              ref={refreshPlansRef}
              segmentName={segmentName}
              onClear={onClearPlanFilter}
              submit={throttledSubmit}
              form={form}
            />
          </div>
        )}
        {['active', 'all'].includes(segmentName) && (
          <div className={styles.contentWrapper}>
            <ActivationDateFilter
              segmentName={segmentName}
              onClear={onClearActivationDateFilter}
              submit={throttledSubmit}
              form={form}
            />
          </div>
        )}
        {['preflight', 'all'].includes(segmentName) && (
          <div className={styles.contentWrapper}>
            <TargetActivationDateFilter
              segmentName={segmentName}
              onClear={onClearTargetActivationDateFilter}
              submit={throttledSubmit}
              form={form}
            />
          </div>
        )}
      </Form>
    );
  }
);

export default SimInventoryFilters;
